import { Box, Button, Grid, Typography, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import DatePicker from 'components/date-picker';
import Dialog from 'components/dialog';
import FlexBox from 'components/flex-box';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs from 'dayjs';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import React, { useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { ProfileType } from 'types/shared/profile-type.enum';
import titleCase from 'utils/title-case';
import { getProfilesAssortment } from 'views/planning-prognosis/api/get-profiles';
import { CreateTemporaryPlanningProfileInput } from 'views/planning-prognosis/features/view-fish-group/types/create-temporary-profile-input.type';
import { UpdateTemporaryPlanningProfileInput } from 'views/planning-prognosis/features/view-fish-group/types/update-temporary-profile-input.type';
import { postTemporaryPlanningProfile } from '../api/temporary-planning/create-temporary-planning-profile';
import { deleteTemporaryPlanningProfile } from '../api/temporary-planning/delete-temporary-planning-profile';
import { getTemporaryPlanningProfiles } from '../api/temporary-planning/get-temporary-planning-profile';
import { putTemporaryPlanningProfiles } from '../api/temporary-planning/update-temporary-planning-profile';
import Paper from 'components/paper';

type Props = {
  fishGroupId: string;
  isEditable: boolean;
};

const TemporaryPlanning: React.FC<Props> = ({ fishGroupId, isEditable }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const assignDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const removeDialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);
  const deleteDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const { account } = useAccountState();
  const theme = useTheme();

  const PAGE_NAME_ID = 'planningPrognosis.viewFishGroup.temporaryPlanningList';

  // FORMS
  const { handleSubmit, control, reset, getValues } = useForm<
    CreateTemporaryPlanningProfileInput | UpdateTemporaryPlanningProfileInput
  >({
    defaultValues: {
      id: 0,
      modelType: ProfileType.EGI,
      modelId: 0,
      from: dayjs().utc(true).toISOString(),
      to: dayjs().utc(true).toISOString(),
    },
    shouldUnregister: true,
  });

  const watch = useWatch({
    control: control,
  });

  // FETCH DATA
  const { data: temporary, isLoading } = useGet(
    ['getPlanAndPrognosisTemporaryProfiles', fishGroupId],
    (context) => getTemporaryPlanningProfiles(String(context.queryKey[1])),
    {
      enabled: !!fishGroupId,
    }
  );

  const { data: profiles } = useGet(
    ['getProfilesAssortment', account.company?.marketId, account.company?.companyId],
    (context) => getProfilesAssortment(Number(context.queryKey[1]), String(context.queryKey[2])),
    {
      enabled: !!account.company?.marketId && !!account.company?.companyId,
    }
  );

  // POST DATA
  const assignTemporaryProfile = usePost<AxiosResponse, CreateTemporaryPlanningProfileInput>(
    (request) => postTemporaryPlanningProfile(fishGroupId, request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getPlanAndPrognosisTemporaryProfiles'],
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      assignDialogRef.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const editTemporaryProfiles = usePost<number, CreateTemporaryPlanningProfileInput | UpdateTemporaryPlanningProfileInput>(
    (request) => putTemporaryPlanningProfiles(fishGroupId, request),
    (response) => {
      [['getPlanAndPrognosisTemporaryProfiles'], ['getFishGroupCalculationStatus']].forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey: queryKey,
        });
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      if (!!response) removeDialogRef.current[response].setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // DELETE
  const deleteTemporaryProfile = usePost<AxiosResponse, number>(
    (request) => deleteTemporaryPlanningProfile(fishGroupId, request),
    (response) => {
      [['getPlanAndPrognosisTemporaryProfiles'], ['getFishGroupCalculationStatus']].forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey: queryKey,
        });
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Success' },
      });
      if (!!response) deleteDialogRef.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: 'Error has occurred', error: error },
      });
    }
  );

  // HANDLERS
  const handleAssignTemporaryProfile = (request: CreateTemporaryPlanningProfileInput) => {
    assignTemporaryProfile.mutate(request);
  };

  const handleEditTemporaryProfiles = (request: CreateTemporaryPlanningProfileInput | UpdateTemporaryPlanningProfileInput) => {
    editTemporaryProfiles.mutate(request);
  };

  const handleDeleteTemporaryProfiles = (temporaryProfileId: number) => {
    deleteTemporaryProfile.mutate(temporaryProfileId);
  };

  // RENDERS
  const renderProfiles = () => {
    if (!profiles) return [];

    switch (getValues('modelType')) {
      case ProfileType.EGI:
        return profiles.egi.filter((p) => p.active && p.isTemporary);
      case ProfileType.EFI:
        return profiles.efi.filter((p) => p.active && p.isTemporary);
      case ProfileType.MORTALITY:
        return profiles.mort.filter((p) => p.active && p.isTemporary);
      case ProfileType.FEED_STRATEGY:
        return profiles.feed.filter((p) => p.active && p.isTemporary);
      case ProfileType.TEMPERATURE:
        return profiles.temp.filter((p) => p.active && p.isTemporary);
      default:
        return [];
    }
  };

  const renderTemporaryProfiles = () => {
    return (
      <>
        <Grid container spacing={4} pt={4}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Select
              fullWidth
              dark
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.type` })}
              margin="none"
              controller={{
                name: 'modelType',
                control: control,
              }}>
              {Object.entries(ProfileType).map((type) => (
                <RadioSelectItem key={type[0]} value={type[1]}>
                  {titleCase(type[0])}
                </RadioSelectItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Select
              fullWidth
              dark
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profile` })}
              controller={{
                name: 'modelId',
                control: control,
              }}
              noResults={{
                show: !renderProfiles().length,
                text: intl.formatMessage({ id: 'general.noResults' }),
              }}>
              {renderProfiles().map((profile) => (
                <RadioSelectItem key={profile.id} value={profile.id} selected={profile.id === watch['modelId']}>
                  {profile.name}
                </RadioSelectItem>
              ))}
            </Select>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              dark
              label={intl.formatMessage({ id: `general.from` })}
              disablePast={true}
              controller={{
                name: 'from',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    isBefore: (v) => (dayjs(v).isBefore(dayjs(watch.to)) ? true : '"From"-date is not before "To"-date'),
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              dark
              label={intl.formatMessage({ id: `general.to` })}
              minDate={dayjs(watch.from)}
              controller={{
                name: 'to',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    isAfter: (v) => (dayjs(v).isAfter(dayjs(watch.from)) ? true : '"To"-date is not after "From"-date'),
                  },
                },
              }}
            />
          </Grid>
          <Box display={'flex'} justifyContent={'end'} width={'100%'}>
            <Button type="submit">
              <FormattedMessage id="general.save" />
            </Button>
          </Box>
        </Grid>
      </>
    );
  };

  return (
    <>
      <Paper transparent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={5}>
          <Typography variant="h3" color={theme.palette.common.white}>
            <FormattedMessage id={`${PAGE_NAME_ID}.title`} />
          </Typography>

          <Dialog
            ref={assignDialogRef}
            maxWidth="sm"
            title={intl.formatMessage({ id: `${PAGE_NAME_ID}.assignTemporary` })}
            button={
              <Button
                disabled={!isEditable}
                onClick={() =>
                  reset({
                    id: 0,
                    modelType: ProfileType.EGI,
                    modelId: 0,
                    from: dayjs().utc(true).toISOString(),
                    to: dayjs().utc(true).toISOString(),
                  })
                }
                sx={{ mb: { xs: 3, md: 0 } }}>
                Assign New
              </Button>
            }>
            <form onSubmit={handleSubmit(handleAssignTemporaryProfile)}>
              {assignTemporaryProfile.isPending && <LoadingSpinner overlay />}
              {renderTemporaryProfiles()}
            </form>
          </Dialog>
        </Box>
        <List
          options={[
            { id: 'name', label: 'Name' },
            { id: 'type', label: 'Type' },
            { id: 'from', label: 'From' },
            { id: 'to', label: 'To' },
            { id: 'actions', label: '', align: 'center', autoWidth: true },
          ]}
          orderBy="name"
          isLoading={isLoading}
          noResult={{
            show: !!temporary && temporary.length <= 0,
            text: <FormattedMessage id={`${PAGE_NAME_ID}.noResults`} />,
          }}>
          {temporary?.map((row, idx) => (
            <ListRow key={idx}>
              <ListCell align="left">{row.name}</ListCell>
              <ListCell align="left">{row.modelType}</ListCell>
              <ListCell align="left">
                <FormattedDate value={row.from} day="2-digit" month="short" year="numeric" />
              </ListCell>
              <ListCell align="left">
                <FormattedDate value={row.to} day="2-digit" month="short" year="numeric" />
              </ListCell>
              <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
                <Box display="flex" justifyContent="space-between" gap={2} alignItems="flex-start">
                  <Dialog
                    ref={(el) => {
                      if (el) return (removeDialogRef.current[idx] = el);
                    }}
                    maxWidth="sm"
                    title={`Delete (${row.name})`}
                    button={
                      <Button type="submit" size="small" disabled={!isEditable} title="Remove">
                        <FormattedMessage id="general.delete" />
                      </Button>
                    }>
                    <Box display={'flex'} flexDirection={'column'} gap={4}>
                      <Box>
                        <FormattedMessage id="general.deleteComment" />
                      </Box>
                      <Box display={'flex'} justifyContent={'end'} gap={4}>
                        <Button variant="outlined" type="submit" size="small" onClick={() => removeDialogRef.current[idx]?.close()}>
                          <FormattedMessage id="general.cancel" />
                        </Button>
                        <Button type="submit" size="small" onClick={() => handleDeleteTemporaryProfiles(row.id)}>
                          <FormattedMessage id="general.delete" />
                        </Button>
                      </Box>
                    </Box>
                  </Dialog>
                  <Dialog
                    ref={(el) => {
                      if (el) removeDialogRef.current[row.id] = el;
                    }}
                    maxWidth="sm"
                    title={intl.formatMessage({ id: `${PAGE_NAME_ID}.editTemporaryProfiles` })}
                    button={
                      <Button
                        size="small"
                        disabled={!isEditable}
                        onClick={() =>
                          reset({
                            id: row.id,
                            modelType: row.modelType,
                            modelId: row.modelId,
                            from: row.from,
                            to: row.to,
                          })
                        }>
                        <FormattedMessage id="general.edit" values={{ type: '' }} />
                      </Button>
                    }>
                    <form onSubmit={handleSubmit(handleEditTemporaryProfiles)}>
                      <FlexBox>
                        {editTemporaryProfiles.isPending && <LoadingSpinner overlay />}
                        <TextField
                          dark
                          fullWidth
                          hidden
                          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.id` })}
                          controller={{
                            name: 'id',
                            control: control,
                          }}
                        />
                        {renderTemporaryProfiles()}
                      </FlexBox>
                    </form>
                  </Dialog>
                </Box>
              </ListCell>
            </ListRow>
          ))}
        </List>
      </Paper>
    </>
  );
};

export default TemporaryPlanning;
